import { faClose } from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import { faAngleRight } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormSegmentedControl } from "@ui/components/FormSegmentedControl";
import { addParameterToURL } from "bm-ui-kit";
import type { ReactNode } from "react";
import { Form } from "react-final-form";
import { AbstractMenu } from "./AbstractMenu";
import { MenuItem } from "./components/MenuItem";
import { MobileMenuTile } from "./components/MobileMenuTile";
import {
	CORPORATE_MOBILE_MENU,
	MAIN_MENU_SELECTOR_OPTIONS,
	MOSAIC_MOBILE_MENU,
	MOSAIC_MOBILE_MENU_CORPORATE,
	RETAIL_MOBILE_MENU,
} from "./data";
import type { Menu, MenuCategory, Variant } from "./type";

const SHOW_LEFT_ANIMATION = "fast slideInLeft d-block";
const SHOW_RIGHT_ANIMATION = "fast slideInRight d-block";
const HIDE_LEFT_ANIMATION = "fast fadeOutLeft d-block";
const HIDE_RIGHT_ANIMATION = "fast fadeOutRightAndHide d-block";

export class MobileMenu extends AbstractMenu {
	private firstRender = true;

	public render(): ReactNode {
		const { variant, openedMenu, openedSubMenu } = this.state;
		const items = variant === "retail" ? RETAIL_MOBILE_MENU : CORPORATE_MOBILE_MENU;

		const { mainLevelAnimation, primaryLevelAnimation, subMenuLevelAnimation } = this.animationController(
			openedMenu,
			openedSubMenu,
		);

		return (
			<div className={`theme-${variant} mobile-menu-holder w-100`}>
				{this.renderMobileTopMenu()}
				<div className="w-100 h-100 position-relative">
					<div className={`slider-lvl-1 position-absolute animated ${mainLevelAnimation}`}>
						<MobileMenuTile
							menuType={variant === "retail" ? MOSAIC_MOBILE_MENU : MOSAIC_MOBILE_MENU_CORPORATE}
							variant={variant}
						/>
						<hr className="mx-3" />
						<div className="mt-1">{items.map((item, key) => this.renderLevelItem(item, key, "main"))}</div>
					</div>
					<div className={`slider-lvl-2 position-absolute animated ${primaryLevelAnimation}`}>
						{openedMenu && (
							<>
								{this.renderLevelMenuHeader(openedMenu.title, "main")}
								<div className="mt-1">
									{openedMenu.items.map((item, key) => this.renderLevelItem(item, key, "primary"))}
								</div>
							</>
						)}
					</div>
					<div className={`slider-lvl-3 position-absolute animated ${subMenuLevelAnimation}`}>
						{openedSubMenu && (
							<>
								{this.renderLevelMenuHeader(openedSubMenu.title, "primary")}

								<div className="mt-1">
									{openedSubMenu.items.map((item, key) =>
										this.renderLevelItem(item, key, "sub-menu"),
									)}
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		);
	}

	protected renderLevelItem(item: Menu, key: number, menuCategory: MenuCategory = "sub-menu"): ReactNode {
		const { variant } = this.state;

		if (this.isHrMenuItem(item)) {
			return <div key={`level2_hr_${key}`} className="hr my-2 mx-2" />;
		}

		const hasSubItems = this.isCategoryMenuItem(item);

		return (
			<MenuItem
				hasSubItems={hasSubItems}
				title={item.title}
				productCount={item.productCount}
				prepend={item.iconElement}
				append={hasSubItems ? undefined : item.badge}
				key={`submenu_${key}`}
				href={
					hasSubItems
						? undefined
						: addParameterToURL(item.url, {
								link_type: `${variant}-top-mobile-menu-${menuCategory}`,
							})
				}
				onClick={() => hasSubItems && this.onSelectSubmenu(item, menuCategory)}
			/>
		);
	}

	private renderLevelMenuHeader(title: string, menuCategory: MenuCategory): ReactNode {
		return (
			<a className="step-back mb-2 p-2" onClick={() => this.onStepBack(menuCategory)}>
				<div className="pl-2">
					<FontAwesomeIcon icon={faAngleRight} className="float-left" rotation={180} />
				</div>
				<div className="pr-1 w-100">{title}</div>
			</a>
		);
	}

	private renderMobileTopMenu(): ReactNode {
		const { variant = "retail" } = this.state;

		return (
			<div className="bg-white d-flex py-1 border-bottom">
				<Form initialValues={{ variant }} onSubmit={(e) => e.preventDefault()}>
					{() => (
						<FormSegmentedControl
							className="mx-3 mb-0"
							fullWidth
							onChange={(value: Variant) => {
								this.setState({ variant: value });
							}}
							options={MAIN_MENU_SELECTOR_OPTIONS}
							name="variant"
						/>
					)}
				</Form>
				<button
					className="border-0 bg-transparent text-black mr-3"
					type="button"
					onClick={() => {
						document.querySelector<HTMLAnchorElement>(".mobile-menu").click();
					}}
				>
					<FontAwesomeIcon icon={faClose} />
				</button>
			</div>
		);
	}

	private onStepBack(menuCategory: MenuCategory): void {
		const { openedMenu } = this.state;

		if (menuCategory === "primary") {
			this.lastOpenedLevel = "sub-menu";
		} else if (menuCategory === "main") {
			this.changeOpenedMenu(undefined);
			this.lastOpenedLevel = "primary";
		}

		this.setState({
			openedMenu: menuCategory === "main" ? undefined : openedMenu,
			openedSubMenu: undefined,
			openedSubMenuLevel2: undefined,
		});
	}

	private animationController(
		openedMenu: Menu,
		openedSubMenu: Menu,
	): { mainLevelAnimation: string; primaryLevelAnimation: string; subMenuLevelAnimation: string } {
		let mainLevelAnimation = openedMenu ? HIDE_LEFT_ANIMATION : SHOW_LEFT_ANIMATION;
		let primaryLevelAnimation = openedMenu ? SHOW_RIGHT_ANIMATION : HIDE_RIGHT_ANIMATION;
		let subMenuLevelAnimation = openedSubMenu ? SHOW_RIGHT_ANIMATION : HIDE_RIGHT_ANIMATION;

		if (this.firstRender) {
			mainLevelAnimation = "d-block";
			this.firstRender = false;
		}

		if (this.lastOpenedLevel === undefined) {
			primaryLevelAnimation = "d-none";
			subMenuLevelAnimation = "d-none";
		} else {
			switch (this.lastOpenedLevel) {
				case "main":
					subMenuLevelAnimation = "d-none";
					break;
				case "primary":
					if (openedSubMenu) {
						primaryLevelAnimation = HIDE_LEFT_ANIMATION;
						mainLevelAnimation = "d-none";
					}

					if (primaryLevelAnimation === HIDE_RIGHT_ANIMATION) {
						subMenuLevelAnimation = "d-none";
					}
					break;
				case "sub-menu":
					if (openedMenu) {
						if (!openedSubMenu) {
							primaryLevelAnimation = SHOW_LEFT_ANIMATION;
							mainLevelAnimation = "d-none";
						} else {
							primaryLevelAnimation = HIDE_RIGHT_ANIMATION;
						}
					}
					break;
				default:
					break;
			}
		}
		this.scrollToMenuTop();

		return { mainLevelAnimation, primaryLevelAnimation, subMenuLevelAnimation };
	}

	private scrollToMenuTop(): void {
		document.body.scrollTop = 0;
		document.documentElement.scrollTop = 0;
	}
}
